import { ref, get } from 'firebase/database';
import { updateGroupStatus, releaseGroupClaims, promoteGroupClaims } from './bookingGroupUtil';
import { sendConfirmationEmail } from './emailServiceUtil';
import { logAuditEvent, AUDIT_ACTION_TYPES, ENTITY_TYPES, GUEST_ACTOR, buildBookingAuditSummary } from './auditLogUtil';
import { hasReceipt } from './receiptUtil';
import { UNSUBMITTED_BOOKING_STATUSES } from '../constants/bookingConst';

/**
 * Firebase side of the checkout step — submitting and releasing a booking.
 *
 * Moved out of App.jsx when checkout moved into its own tab: the checkout tab
 * now performs these writes itself (so a booking still gets submitted even if
 * the booking tab was closed), and the booking tab uses the same functions
 * when it has to release a reservation on its own.
 */

/**
 * True when a booking in hand has already been submitted (receipt attached).
 * Uses hasReceipt() so both receipt shapes count — the `hasPaymentScreenshot`
 * flag (receipt stored in bookingReceipts/) and the legacy inline
 * `paymentScreenshotUrl`.
 * @param {Object|null} bookingData
 * @returns {boolean}
 */
export const isBookingSubmitted = (bookingData) =>
  !!bookingData && (bookingData.status === 'pending' || hasReceipt(bookingData));

/**
 * Reads a booking record's current status.
 * @param {Object} db
 * @param {string} bookingId
 * @returns {Promise<string|null>} the status, or null if the record is missing.
 */
const readBookingStatus = async (db, bookingId) => {
  const snap = await get(ref(db, `bookings/${bookingId}/status`));
  return snap.exists() ? snap.val() : null;
};

/**
 * Submits a booking once the guest has attached a payment receipt: flips
 * every date-record to 'pending' (awaiting admin review), makes the slot and
 * equipment holds permanent, emails the admin, and writes the
 * audit entry.
 *
 * The audit entry is logged here, at receipt upload, and not when the record
 * is first written. Before this point the record only holds the slot during
 * checkout, and most of those are abandoned carts that an admin never acts
 * on — logging them buried the bookings that matter.
 *
 * @param {Object} params
 * @param {Object} params.db
 * @param {Object} params.bookingData - the booking, already carrying status 'pending'.
 * @param {string[]} params.bookingKeys - one record id per date, index-aligned with bookingData.dates.
 * @param {Object} params.config - club config (admin email, email settings).
 * @returns {Promise<void>}
 */
export const finalizeSubmittedBooking = async ({ db, bookingData, bookingKeys, config }) => {
  if (bookingKeys && bookingKeys.length > 0 && db) {
    try {
      await updateGroupStatus(db, bookingKeys, 'pending');
      if (bookingData.dates) {
        await promoteGroupClaims(db, bookingData.dates, bookingKeys);
      }
    } catch (error) {
      console.error('Error finalizing booking status:', error);
    }
  }
  // Admin only: a new booking still needs review, so the guest is emailed
  // later, when an admin confirms it (see AdminBooking.jsx). Sent even when
  // the guest left email blank, since the admin copy does not depend on it.
  sendConfirmationEmail(bookingData, config?.adminEmail, false, config, { notifyGuest: false }).catch(() => {});
  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.BOOKING_SUBMITTED,
    entityType: ENTITY_TYPES.BOOKING,
    entityId: bookingData.groupId || (bookingKeys && bookingKeys[0]),
    performedBy: bookingData.userName || GUEST_ACTOR,
    statusOld: 'awaiting_payment',
    statusNew: 'pending',
    summary: buildBookingAuditSummary(bookingData),
  });
};

/**
 * Releases an unsubmitted reservation: marks every date-record cancelled and
 * frees its slot and equipment holds.
 *
 * Checks the stored status first. With checkout in a separate tab, two tabs
 * can act on the same booking — e.g. the booking tab's timer runs out moments
 * after the receipt went in from the checkout tab. A booking that was already
 * submitted must never be cancelled, so that case is reported back instead.
 * If the status can't be read (offline), the release goes ahead as before.
 *
 * Not written to the audit trail, on purpose: an abandoned or timed-out
 * checkout was never a submitted booking (see finalizeSubmittedBooking).
 *
 * @param {Object} params
 * @param {Object} params.db
 * @param {Object|null} params.bookingData
 * @param {string[]|null} params.bookingKeys
 * @param {string} params.cancelReason - e.g. 'guest_cancelled', 'payment_timeout'.
 * @returns {Promise<{ alreadySubmitted: boolean }>}
 */
export const releasePendingBooking = async ({ db, bookingData, bookingKeys, cancelReason }) => {
  if (isBookingSubmitted(bookingData)) return { alreadySubmitted: true };
  if (!db || !bookingKeys || bookingKeys.length === 0) return { alreadySubmitted: false };

  let storedStatus = null;
  try {
    storedStatus = await readBookingStatus(db, bookingKeys[0]);
  } catch (error) {
    console.error('Error reading booking status:', error);
  }
  if (storedStatus && !UNSUBMITTED_BOOKING_STATUSES.includes(storedStatus)) {
    return { alreadySubmitted: true };
  }
  if (storedStatus === 'cancelled') {
    // Already released by the other tab.
    return { alreadySubmitted: false };
  }

  try {
    await updateGroupStatus(db, bookingKeys, 'cancelled', { cancelReason });
    if (bookingData?.dates) {
      await releaseGroupClaims(db, bookingData.dates, bookingKeys);
    }
  } catch (error) {
    console.error('Error releasing booking:', error);
  }
  return { alreadySubmitted: false };
};
